/**
 * App_Floodgate.ts
 *
 * The entry point for floodgate.
 */

import * as App_Common from "./App_Common";
import * as UI from "./UI/UI";
import * as Configuration from "./Configuration/Configuration";
import * as Window from "./Window/Window";
import AdaptiveSurveyLauncherFactory from "./FloodgateCore/AdaptiveSurveyLauncherFactory";
import CustomUISurvey from "./FloodgateCore/UISurvey/CustomUISurvey";
import DummyUISurvey from "./FloodgateCore/UISurvey/DummyUISurvey";
import FloodgateEnvironmentProvider from "./FloodgateCore/FloodgateEnvironmentProvider";
import FloodgateStorageProvider from "./FloodgateCore/FloodgateStorageProvider";
import FloodgateStringProvider from "./FloodgateCore/FloodgateStringProvider";
import FloodgateTelemetryLogger from "./FloodgateCore/FloodgateTelemetryLogger";
import ICustomSurvey from "./FloodgateCore/ICustomSurvey";
import IUISurvey from "./FloodgateCore/UISurvey/IUISurvey";

import { FloodgateEngine } from "@ms-ofb/officefloodgatecore";
import { FilterValidCampaignDefinitions } from
	"@ms-ofb/officefloodgatecore/dist/src/Campaign/CampaignDefinitionProvider";
import { IFloodgateStorageProvider } from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";

import { Promise } from "es6-promise";

/**
 * Has floodgate been initialized
 */
let initialized: boolean = false;

/**
 * Has the control been opened. Prevents more than one dialog being generated.
 */
let opened: boolean = false;

let engine: FloodgateEngine;

/**
 * Initialize. Must be called before any other call to floodgate.
 * @returns A promise which will be rejected if the call fails.
 */
export function initialize(): Promise<any> {
	return new Promise((resolve, reject) => {
		if (!FloodgateStorageProvider.isStorageAvailable()) {
			reject("window.localStorage is not available.");
			return;
		}

		if (!initialized) {
			App_Common.initialize()
			.then(
				function onInitializeFulfilled() {
					Window.get().OfficeBrowserFeedback.floodgate = Window.get().OfficeBrowserFeedback.floodgate || {} as any;

					let floodgateInitOptions = Window.get().OfficeBrowserFeedback.floodgate.initOptions;
					floodgateInitOptions = floodgateInitOptions || {} as any;

					// Validate and copy campaign definitions to local storage 
					if (floodgateInitOptions.campaignDefinitions !== undefined) {
						const filteredResult = FilterValidCampaignDefinitions(floodgateInitOptions.campaignDefinitions);

						if (filteredResult.error) {
							reject("floodgate.initOptions.campaignDefinitions error: " + filteredResult.error);
							return;
						}

						(new FloodgateStorageProvider()).write(
							IFloodgateStorageProvider.FileType.CampaignDefinitions,
							JSON.stringify(floodgateInitOptions.campaignDefinitions)
						);
					}

					Configuration.get().setFloodgateInitOptions(Window.get().OfficeBrowserFeedback.floodgate.initOptions);

					FloodgateEngine.setTelemetryLogger(new FloodgateTelemetryLogger());

					initialized = true;
					resolve();
				}
			)
			.catch(
				function onInitializeRejected(err) {
					reject("Initialization failed: {" + err + "}");
				}
			);
		} else {
			resolve();
		}
	});
}

/**
 * To support the multi-window scenario on web we need to initialize the engine on each resume.
 * Hence this seperate method which does make() and start() together.
 */
export function start(): Promise<any> {
	return new Promise((resolve, reject) => {
		try {
			engine = FloodgateEngine.make(
				Configuration.get().getCommonInitOptions().build ? Configuration.get().getCommonInitOptions().build : "",
				new AdaptiveSurveyLauncherFactory(),
				Configuration.get().getFloodgateInitOptions().onSurveyActivatedCallback,
				new FloodgateStorageProvider(),
				new FloodgateStringProvider(),
				new FloodgateEnvironmentProvider()
			);

			engine.start();
		} catch (e) {
			reject("Failed to start floodgate engine: " + e.toString());
			return;
		}

		resolve();
	});
}

/**
 * A proxy for the engine.stop() method for consistency with the start() method.
 */
export function stop() {
	if (engine) {
		engine.stop();
	}
}

/**
 * Get the engine object
 * @returns the engine object
 */
export function getEngine(): FloodgateEngine {
	return engine;
}

/**
 * Display the given Survey with it's prompt
 * @param survey the survey to show
 * @returns A promise which will be rejected if the call fails.
 */
export function showSurvey(survey: IUISurvey): Promise<any> {
	return new Promise((resolve, reject) => {
		if (opened) {
			reject("Control already open");
			return;
		}

		Configuration.get().setFloodgateSurvey(survey ? survey : new DummyUISurvey());
		UI.createSurvey(function () { opened = false; });
		opened = true;
		resolve();
	});
}

/**
 * Method to allow users to launch a custom survey directly
 * @param survey the custom survey to show
 */
export function showCustomSurvey(survey: ICustomSurvey): Promise<any> {
	return showSurvey(new CustomUISurvey(survey));
}

/**
 * Reset the floodgate module. Used in unit tests.
 */
export function reset() {
	App_Common.reset();
	initialized = false;
	opened = false;
}

Window.setFloodgateShowCustomSurvey(showCustomSurvey);
Window.setFloodgateShowSurvey(showSurvey);
Window.setFloodgateGetEngine(getEngine);
Window.setFloodgateInitialize(initialize);
Window.setFloodgateStart(start);
Window.setFloodgateStop(stop);
